import { Injectable } from '@angular/core';
import { ApiService, GetWeekMenu$Request } from '../api/api.service';
import { Observable, Subject, ReplaySubject, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  GetWeekMenu$Response,
  GetRecipes$Response,
  MealId,
  AddMeal$Response,
  GetAllergens$Response,
  SwapRecipe$Response,
  GetDailyMenu$Response,
  DailyMenu,
  ImageSizeValues,
  GetFamilyAllergens$Response,
  SearchRecipeSwap$Response,
  RecipeTag,
  IngredientId,
  RecipeId,
  GetRandomRecipes$Response,
  Lang,
  ShareMeals$Response,
  ShareMenu$Response,
  LocalDate
} from '@api/types';

@Injectable()
export class RecipesService {
  private recipes: any = {};
  private weeklyMenu: DailyMenu[];
  private randomReplacementMealIdArray: RecipeId[] = [];
  private weekMenuSubject: Subject<GetWeekMenu$Response> = new ReplaySubject<GetWeekMenu$Response>(null);

  private imageExtension: string = '.jpg';

  constructor(private api: ApiService) {}

  getWeekMenu(forceFetch?: boolean, date?: string): Observable<GetWeekMenu$Response> {
    if (!this.weeklyMenu || forceFetch) {
      let request: GetWeekMenu$Request = {
        date: date ? date : null
      };

      this.api.menu.getWeekMenu(request).subscribe(response => {
        if (response && response.weekMenu) {
          this.weeklyMenu = response.weekMenu;
          this.weekMenuSubject.next({
            weekMenu: this.weeklyMenu
          });
        }
      });
    } else {
      this.weekMenuSubject.next({
        weekMenu: this.weeklyMenu
      });
    }

    return this.weekMenuSubject;
  }

  getDailyMenu(date: string, direction: string = 'prev'): Observable<GetDailyMenu$Response> {
    let request = {
      date: date,
      getNextIfNotFound: true,
      direction: direction === 'prev' ? -1 : 1
    };
    let obs = this.api.menu.getDailyMenu(request);

    obs.subscribe(response => {
      if (response && response.dailyMenu && response.dailyMenu.meals.length) {
        if (direction == 'next') {
          this.weeklyMenu.push(response.dailyMenu);
        } else {
          this.weeklyMenu.unshift(response.dailyMenu);
        }
        this.weekMenuSubject.next({
          weekMenu: this.weeklyMenu
        });
      }
    });

    return obs;
  }

  performSearch(recipeName: string, mealId: MealId, tagArray: RecipeTag[], ingredientArray: IngredientId[]): Observable<SearchRecipeSwap$Response> {
    let request = {
      ingredientIds: ingredientArray,
      mealId: mealId,
      recipeName: recipeName,
      tags: tagArray
    };

    return this.api.recipe.searchRecipeSwap(request);
  }

  getSwapData(mealId: MealId): Observable<GetRecipes$Response> {
    return this.api.meal.getRecipes({ mealId: mealId });
  }

  shareMeals(email: string, mealIds: MealId[]): Observable<ShareMeals$Response> {
    let req = {
      email: email,
      meals: mealIds
    };

    return this.api.meal.shareMeals(req);
  }
  shareMenu(email: string, localDates: string[]): Observable<ShareMenu$Response> {
    const req = {
      days: <any>localDates,
      email: email
    };
    return this.api.menu.shareMenu(req);
  }

  /*
        Get all replaceable recipe ids or if we have them cached then request set number of recipes using the ids
     */
  getRandomReplacementMeals(mealId: MealId, mealsToGet: number = 10, excludedIds: RecipeId[] = []): Observable<GetRandomRecipes$Response> {
    mealsToGet = mealsToGet > 10 ? 10 : mealsToGet <= 0 ? 1 : mealsToGet; // we can get recipes in range of 1 -

    if (!this.randomReplacementMealIdArray.length) {
      // we don't have cached replacement recipeIds
      let obs = this.api.meal.getRandomRecipes({
        mealId: mealId,
        recipeIds: null
      });

      // first get all replacable recipe id-s, cache them and then call this function again
      return obs.pipe(
        switchMap(res => {
          if (res) {
            this.randomReplacementMealIdArray = res.recipeIds;
            return this.getRandomReplacementMeals(mealId, mealsToGet, excludedIds);
          }
          return of(null);
        })
      );
    } else {
      // we already have randomReplacementMealId-s cached so we fetch actual recipes
      let filteredIdArray: RecipeId[] = this.randomReplacementMealIdArray;

      // filter out excluded items
      if (excludedIds.length) {
        filteredIdArray = this.randomReplacementMealIdArray.filter(i => {
          return excludedIds.indexOf(i) === -1;
        });
      }

      // select only a certain amount of recipes
      let finalSelectedIdArray: RecipeId[] = filteredIdArray.slice(0, mealsToGet);

      return this.api.meal.getRandomRecipes({
        mealId: mealId,
        recipeIds: finalSelectedIdArray
      });
    }
  }

  /*
        Clears randomReplacementMealIdArray from cache
     */
  clearRandomReplacementMealIdArray(): void {
    this.randomReplacementMealIdArray = [];
  }

  getAllergens(lang: Lang = null): Observable<GetAllergens$Response> {
    let obs = this.api.allergy.getAllergens({ lang: lang });

    obs.subscribe(res => {});

    return obs;
  }

  getFamilyAllergens(): Observable<GetFamilyAllergens$Response> {
    let obs = this.api.family.getFamilyAllergens();

    obs.subscribe(res => {});

    return obs;
  }

  addMeal(request): Observable<AddMeal$Response> {
    return this.api.meal.addMeal(request);
  }

  swapRecipe(mealId: MealId, recipeId: RecipeId): Observable<SwapRecipe$Response> {
    let request = {
      mealId: mealId,
      recipeId: recipeId
    };

    let obs = this.api.meal.swapRecipe(request);

    return obs;
  }

  getUnusedRandomRecipeList(alreadyUsedRecipes: RecipeId[]): RecipeId[] {
    return this.randomReplacementMealIdArray.filter(i => {
      return !alreadyUsedRecipes.find(o => {
        return o === i;
      });
    });
  }

  getImageMD(baseUrl: string) {
    return baseUrl + ImageSizeValues.IMAGE_ORIGINAL + this.imageExtension + '?w=762';
  }
  getImageSMHD(baseUrl: string) {
    return baseUrl + ImageSizeValues.IMAGE_SQUARE + this.imageExtension + '?w=140';
  }
  getImageSM(baseUrl: string) {
    return baseUrl + ImageSizeValues.IMAGE_SQUARE + this.imageExtension + '?w=70';
  }
  getImageXS(baseUrl: string) {
    return baseUrl + ImageSizeValues.IMAGE_SQUARE + this.imageExtension + '?w=30';
  }
}
